/**
 * MethodologyTooltip - Expandable info panel for methodology details.
 */

import React, { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Info as InfoIcon } from '@mui/icons-material';
import { vortxTypography } from '@vortx/shared-ui';
import { COLORS, METHODOLOGY_CONTENT } from './ResultsDesignTokens';

// Methodology Tooltip - expandable info panel
export const MethodologyTooltip = ({ metricKey, children }) => {
    const [expanded, setExpanded] = useState(false);
    const methodology = METHODOLOGY_CONTENT[metricKey];

    if (!methodology) return children;

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 0,
                }}
            >
                {children}
                <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 28,
                        bgcolor: alpha(COLORS.accent, 0.1),
                        borderRadius: '0 8px 8px 0',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        borderLeft: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                        '&:hover': {
                            bgcolor: alpha(COLORS.accent, 0.2),
                        },
                    }}
                >
                    <InfoIcon sx={{
                        fontSize: 14,
                        color: expanded ? COLORS.accent : COLORS.text.muted,
                        transition: 'color 0.2s ease',
                    }} />
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: alpha(COLORS.accent, 0.08),
                    borderRadius: 1,
                    border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: COLORS.accent,
                        mb: 1,
                    }}>
                        {methodology.title}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.text.secondary,
                        lineHeight: 1.6,
                        mb: 1.5,
                    }}>
                        {methodology.description}
                    </Typography>
                    <Box sx={{
                        p: 1,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        borderRadius: 1,
                        mb: 1.5,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.efficiency,
                            textAlign: 'center',
                        }}>
                            {methodology.formula}
                        </Typography>
                    </Box>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: COLORS.text.muted,
                        fontStyle: 'italic',
                        lineHeight: 1.5,
                    }}>
                        Note: {methodology.note}
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
};
